import axios from 'axios';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';

// #region agent log
console.log('[DEBUG] API_BASE_URL =', API_BASE_URL);
console.log('[DEBUG] REACT_APP_API_URL env =', process.env.REACT_APP_API_URL);
// #endregion

const api = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  },
});

// #region agent log
// Debug interceptors to log request/response details
api.interceptors.request.use((config) => {
  console.log(`[DEBUG][REQ] ${config.method?.toUpperCase()} ${config.baseURL}${config.url}`, config.params || '');
  return config;
});

api.interceptors.response.use(
  (response) => {
    const dataPreview = JSON.stringify(response.data)?.substring(0, 200);
    console.log(`[DEBUG][RES] ${response.status} ${response.config.url} | data type: ${typeof response.data} | array: ${Array.isArray(response.data)} | length: ${Array.isArray(response.data) ? response.data.length : 'N/A'} | preview: ${dataPreview}`);
    return response;
  },
  (error) => {
    console.error(`[DEBUG][ERR] ${error.config?.url} | message: ${error.message} | code: ${error.code} | response status: ${error.response?.status}`);
    if (error.response) {
      console.error(`[DEBUG][ERR] response headers:`, Object.fromEntries(Object.entries(error.response.headers || {})));
    }
    return Promise.reject(error);
  }
);
// #endregion

// ============================================================================
// Existing API Services
// ============================================================================

// Countries API
export const countriesAPI = {
  getAll: (params) => api.get('/countries', { params }),
  getById: (id) => api.get(`/countries/${id}`),
  getProfile: (id) => api.get(`/countries/${id}/profile`),
};

// Tariffs API
export const tariffsAPI = {
  getAll: (params) => api.get('/tariffs', { params }),
  getTrends: (params) => api.get('/tariffs/trends', { params }),
  compare: (params) => api.get('/tariffs/compare', { params }),
  getCategories: () => api.get('/tariffs/categories'),
};

// Trade Deals API
export const tradeDealsAPI = {
  getAll: (params) => api.get('/trade-deals', { params }),
  getById: (id) => api.get(`/trade-deals/${id}`),
};

// Duties API
export const dutiesAPI = {
  getAll: (params) => api.get('/duties', { params }),
};

// Legislation API
export const legislationAPI = {
  getAll: (params) => api.get('/legislation', { params }),
  getById: (id) => api.get(`/legislation/${id}`),
  getStats: (params) => api.get('/legislation/stats/summary', { params }),
};

// Alerts API
export const alertsAPI = {
  create: (data) => api.post('/alerts', data),
  getAll: (params) => api.get('/alerts', { params }),
  update: (id, data) => api.put(`/alerts/${id}`, data),
  delete: (id) => api.delete(`/alerts/${id}`),
};

// Stats API
export const statsAPI = {
  getGlobal: () => api.get('/stats/global'),
  getTopCountries: (params) => api.get('/stats/tariffs/top-countries', { params }),
};

// Export API
export const exportAPI = {
  tariffsCSV: (params) => api.get('/export/tariffs/csv', { params, responseType: 'blob' }),
  legislationPDF: (params) => api.get('/export/legislation/pdf', { params, responseType: 'blob' }),
};

// News API
export const newsAPI = {
  getAll: (params) => api.get('/news', { params }),
  getTrending: (params) => api.get('/news/trending', { params }),
  getByCategory: (category, params) => api.get(`/news/category/${category}`, { params }),
  getByCountry: (countryCode, params) => api.get(`/news/country/${countryCode}`, { params }),
};

// ============================================================================
// TradeRadar v2 — GTI & Trade Events API
// ============================================================================

// Global Trade Index (GTI) API
export const gtiAPI = {
  /** Get all country GTI scores (latest). Params: { limit, sort, trend } */
  getCountries: (params) => api.get('/gti/countries', { params }),

  /** Get single country GTI with sector breakdown. */
  getCountry: (code) => api.get(`/gti/countries/${code}`),

  /** Get global sector benchmarks. */
  getSectors: () => api.get('/gti/sectors'),

  /** Get all countries for a specific sector. Params: { limit } */
  getSectorDetail: (sector, params) => api.get(`/gti/sectors/${sector}`, { params }),

  /** Get top/bottom country rankings. Params: { top, bottom } */
  getRankings: (params) => api.get('/gti/rankings', { params }),

  /** Get GTI history for a country over time. Params: { days } */
  getHistory: (code, params) => api.get(`/gti/history/${code}`, { params }),
};

// Trade Events API (AI-extracted structured intelligence)
export const tradeEventsAPI = {
  /** List trade events with filters. Params: { limit, offset, event_type, sector, ... } */
  getAll: (params) => api.get('/trade-events', { params }),

  /** Get single trade event detail with linked news. */
  getById: (id) => api.get(`/trade-events/${id}`),

  /** Get aggregated event statistics. Params: { days } */
  getStats: (params) => api.get('/trade-events/stats', { params }),

  /** Get events timeline for charting. Params: { days, group_by } */
  getTimeline: (params) => api.get('/trade-events/timeline', { params }),
};

// Pipeline Status API
export const pipelineAPI = {
  /** Get recent pipeline run statuses. */
  getStatus: () => api.get('/pipeline/status'),

  /** Health check with DB status. */
  health: () => api.get('/health'),
};

// ============================================================================
// Chokepoints API (Hormuz, Red Sea, Suez, Panama)
// ============================================================================
export const chokepointsAPI = {
  /** Get overview/pillar page data for a region */
  getOverview: (region = 'hormuz') => api.get(`/chokepoints/${region}/overview`),

  /** Get statistics for a region */
  getStatistics: (region = 'hormuz') => api.get(`/chokepoints/${region}/statistics`),

  /** Get news for a region. Params: { limit, offset, start_date, end_date } */
  getNews: (region = 'hormuz', params) => api.get(`/chokepoints/${region}/news`, { params }),

  /** Get current risk score for a region */
  getRiskScore: (region = 'hormuz') => api.get(`/chokepoints/${region}/risk-score`),

  /** Get historical timeline for a region. Params: { limit, start_year, end_year } */
  getTimeline: (region = 'hormuz', params) => api.get(`/chokepoints/${region}/timeline`, { params }),

  /** Calculate oil impact. Data: { disruptionPercent, durationDays, basePrice } */
  calculateImpact: (region = 'hormuz', data) => api.post(`/chokepoints/${region}/calculate-impact`, data),

  /** Get diagnostic information about risk score calculation */
  getDiagnostics: (region = 'hormuz') => api.get(`/chokepoints/${region}/diagnostics`),
};

// ============================================================================
// Dashboard API (Middle East Impact Dashboard)
// ============================================================================
export const dashboardAPI = {
  /** Get summary data for "This Hour" bar */
  getSummary: () => api.get('/dashboard/middle-east/summary'),

  /** Get current and historical oil prices */
  getOilPrices: () => api.get('/dashboard/middle-east/oil-prices'),

  /** Get vessel traffic data with risk level */
  getVesselCounts: () => api.get('/dashboard/middle-east/vessel-counts'),

  /** Get air traffic data */
  getFlightCounts: () => api.get('/dashboard/middle-east/flight-counts'),

  /** Get categorized news feed. Params: { limit } */
  getNews: (params) => api.get('/dashboard/middle-east/news', { params }),

  /** Get social sentiment data */
  getSentiment: () => api.get('/dashboard/middle-east/sentiment'),
};

export default api;
